// Gemeinsame Bausteine der Ansichten „Meilensteine“ und „Offene Punkte“ (beide per lazy import geladen).
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api } from '../api';
import { darfOderSa } from '../sync';
import { useIch } from '../App';
import { Choice, type Option } from './fields';
import { Icon } from './Icon';
import '../styles/projektsteuerung.css';

export interface Person { email: string; name?: string | null; firma?: string | null }

/** Bekannte Personen für „Verantwortlich“: ich, Mitglieder (lesbar ab mitwirkend) und schon eingetragene Werte. */
export function usePersonen(projektId: string, eingetragen: (string | null | undefined)[]) {
  const { ich } = useIch();
  const [mitglieder, setMitglieder] = useState<Person[]>([]);
  useEffect(() => {
    if (!darfOderSa('mitwirkend', ich?.superadmin)) return;
    api.personen(projektId).then((m) => setMitglieder(m.personen)).catch(() => { /* ohne Liste: bekannte Personen */ });
  }, [projektId]);
  const schluessel = eingetragen.join('|');
  const personen = useMemo(() => {
    const m = new Map<string, Person>();
    const dazu = (email: string | null | undefined, name?: string | null, firma?: string | null) => {
      if (!email || !email.includes('@')) return;
      const k = email.toLowerCase();
      const alt = m.get(k);
      if (!alt || (name && !alt.name) || (firma && !alt.firma)) m.set(k, { email: k, name: alt?.name || name, firma: alt?.firma || firma });
    };
    if (ich) dazu(ich.email, ich.name);
    for (const x of mitglieder) dazu(x.email, x.name, x.firma);
    for (const e of eingetragen) dazu(e);
    return [...m.values()].sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));
  }, [schluessel, mitglieder, ich]);
  const personName = (z: string) => personen.find((p) => p.email === z.toLowerCase())?.name || z;
  return { personen, personName };
}

/** Verantwortlich: Person wählen oder E-Mail/Namen frei eintragen; meldet jeden Stand (gespeichert wird außerhalb). */
export function PersonWahl({ wert, personen, onChange, testid }: { wert: string | null; personen: Person[]; onChange: (v: string | null) => void; testid: string }) {
  const optionen: Option[] = personen.map((p) => ({ value: p.email, label: p.name ? `${p.name} (${p.email})` : p.email }));
  return <Choice free value={wert ?? ''} options={optionen} placeholder="Person wählen oder Namen eintragen …" testid={testid}
    onChange={(v: string | null) => onChange(v?.trim() || null)} />;
}

/** Wie PersonWahl, speichert aber erst bei Auswahl bzw. beim Verlassen des Felds (für Detailbereiche ohne Speichern-Knopf). */
export function PersonFeld({ wert, personen, onCommit, testid }: { wert: string | null; personen: Person[]; onCommit: (v: string | null) => void; testid: string }) {
  const entwurf = useRef<string | null>(wert);
  useEffect(() => { entwurf.current = wert; }, [wert]);
  const speichern = (v: string | null) => { if ((v ?? '') !== (wert ?? '')) onCommit(v); };
  return (
    <div onBlur={() => setTimeout(() => speichern(entwurf.current), 200)}>
      <PersonWahl wert={wert} personen={personen} testid={testid}
        onChange={(v) => { entwurf.current = v; if (personen.some((p) => p.email === v)) speichern(v); }} />
    </div>
  );
}

/** Modaler Dialog: schließt nur über ✕, Abbrechen oder Esc – nie per Klick daneben. */
/** Modaler Dialog; gross = 80 % von Fensterbreite und -höhe (z. B. Protokoll), Inhalt füllt die Höhe; mit breit 90 % Breite. */
export function Dialog({ titel, onClose, children, testid, breite = 620, gross, breit }: { titel: string; onClose: () => void; children: ReactNode; testid: string; breite?: number; gross?: boolean; breit?: boolean }) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);
  return (
    <div className="overlay" role="presentation">
      <div className={'dialog' + (gross ? ' gross' : '') + (breit ? ' breit' : '')} role="dialog" aria-modal="true" aria-label={titel} data-testid={testid}
        style={gross ? undefined : { maxWidth: breite, width: `min(${breite}px, 100%)` }}>
        <button className="btn icon close" onClick={onClose} title="Schließen (Esc)" data-testid="dialog-schliessen"><Icon name="x" /></button>
        <h2>{titel}</h2>
        {children}
      </div>
    </div>
  );
}
